import { DatePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { forkJoin } from 'rxjs';
import { ApiService } from 'src/app/services/api.service';
import { UtilitiesService } from 'src/app/services/utilities.service';
import Swal from 'sweetalert2';

interface LeaveForm {
  employee_id: string | null;
  employee_name: string | null;
  from_date: string | null;
  to_date: string | null;
  leave_type: string;
  notes: string | null;
  hours: number | null;
}

@Component({
  selector: 'app-attendance',
  templateUrl: './attendance.component.html',
  styleUrls: ['./attendance.component.css'],
  standalone: false
})
export class AttendanceComponent implements OnInit {

  page = 1;
  pageSize = 10;

  allItems: any[] = [];
  displayItems: any[] = [];
  employees: any[] = [];

  list_length = 0;
  filter_date: string | null = null;
  searchTerm = '';

  token: string | null;
  modalMode: 'create' | 'edit' = 'create';
  editingId: string | null = null;
  submitting = false;
  uploading = false;
  selectedLeave: any = null;
  files: FileList | null = null;

  form: LeaveForm = this.emptyForm();
  durationMode: 'days' | 'hours' = 'days';

  readonly hourOptions = [
    { value: 1, label: '1 hour' },
    { value: 2, label: '2 hours' },
    { value: 3, label: '3 hours' },
    { value: 4, label: '4 hours (half day)' },
    { value: 5, label: '5 hours' },
    { value: 6, label: '6 hours' },
    { value: 7, label: '7 hours' },
    { value: 8, label: '8 hours (full day)' }
  ];

  readonly leaveTypes = [
    { value: 'ANNUAL', label: 'Annual', badge: 'bg-primary' },
    { value: 'SICK', label: 'Sick', badge: 'bg-warning text-dark' },
    { value: 'PERSONAL', label: 'Without Payment', badge: 'bg-secondary' },
    { value: 'LEAVE', label: 'Leave', badge: 'bg-info text-dark' },
    { value: 'LONG', label: 'Long', badge: 'bg-dark' },
    { value: 'OTHER', label: 'Other', badge: 'bg-light text-dark border' }
  ];

  constructor(
    public datepipe: DatePipe,
    public utility: UtilitiesService,
    private modalService: NgbModal,
    private api: ApiService
  ) {
    this.utility.show = true;
    this.token = localStorage.getItem('access_token');
  }

  ngOnInit(): void {
    this.loadData();
  }

  get paginatedItems(): any[] {
    const start = (this.page - 1) * this.pageSize;
    return this.displayItems.slice(start, start + this.pageSize);
  }

  get isFormValid(): boolean {
    return !!(
      this.form.employee_id &&
      this.form.from_date &&
      this.form.to_date &&
      this.form.leave_type &&
      this.form.leave_type !== '0' &&
      this.previewDays !== null &&
      this.previewDays > 0
    );
  }

  get previewDays(): number | null {
    if (this.durationMode === 'hours') {
      if (!this.form.from_date || !this.form.hours) {
        return this.form.from_date ? 0 : null;
      }
      return this.form.hours / 8;
    }
    if (!this.form.from_date || !this.form.to_date) {
      return null;
    }
    return this.calcDays(this.form.from_date, this.form.to_date);
  }

  setDurationMode(mode: 'days' | 'hours'): void {
    this.durationMode = mode;
    if (mode === 'hours') {
      this.form.hours = this.form.hours || 4;
      this.form.to_date = this.form.from_date;
    } else {
      this.form.hours = null;
    }
  }

  onFromDateChange(): void {
    if (this.durationMode === 'hours') {
      this.form.to_date = this.form.from_date;
    }
  }

  formatLeaveDuration(item: any): string {
    const hours = this.parseHours(item?.hours);
    if (hours) {
      return hours === 4 ? '0.5 day' : `${hours} hrs`;
    }
    return String(item?.dayDiff ?? 0);
  }

  openCreate(modal: unknown): void {
    this.modalMode = 'create';
    this.editingId = null;
    this.durationMode = 'days';
    this.form = this.emptyForm();
    this.modalService.open(modal, { size: 'lg', centered: true, backdrop: 'static' });
  }

  openEdit(modal: unknown, leave: any): void {
    this.modalMode = 'edit';
    this.editingId = leave.id;
    const hours = this.parseHours(leave.hours);
    this.durationMode = hours ? 'hours' : 'days';
    this.form = {
      employee_id: leave.employee_id,
      employee_name: leave.employee_name,
      from_date: this.toInputDate(leave.from_date),
      to_date: this.toInputDate(leave.to_date),
      leave_type: leave.leave_type ?? 'ANNUAL',
      notes: leave.notes,
      hours
    };
    this.modalService.open(modal, { size: 'lg', centered: true, backdrop: 'static' });
  }

  openLeaveFiles(modal: unknown, leave: any): void {
    this.selectedLeave = leave;
    this.files = null;
    this.modalService.open(modal, { size: 'lg', centered: true, backdrop: 'static' });
  }

  fileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.files = input.files;
  }

  submitLeaveFiles(): void {
    if (!this.selectedLeave?.id) {
      return;
    }

    if (!this.files || this.files.length === 0) {
      Swal.fire({
        title: 'Warning!',
        text: 'No files selected!',
        icon: 'warning',
        confirmButtonText: 'OK'
      });
      return;
    }

    const formData = new FormData();
    for (const file of Array.from(this.files)) {
      formData.append('file', file, file.name);
    }

    this.uploading = true;
    this.api.upload('leaves/upload/' + this.selectedLeave.id + '/1', formData).subscribe({
      next: () => {
        Swal.fire({
          title: 'Success!',
          text: 'File uploaded successfully!',
          icon: 'success',
          confirmButtonText: 'OK',
          timer: 2000
        });
        this.files = null;
        this.loadData();
      },
      error: () => {
        Swal.fire({
          title: 'Error!',
          text: 'Could not upload the file. Please try again.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      },
      complete: () => {
        this.uploading = false;
      }
    });
  }

  onDeleteLeaveFile(file: any): void {
    const fileId = this.getFileId(file);
    if (!fileId) {
      return;
    }

    Swal.fire({
      title: 'Delete attachment?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel'
    }).then(result => {
      if (!result.isConfirmed) {
        return;
      }

      this.api.delete('leaves/upload/' + fileId, this.token).subscribe({
        next: () => {
          Swal.fire({
            title: 'Deleted',
            text: 'Attachment removed.',
            icon: 'success',
            timer: 2000,
            showConfirmButton: false
          });
          this.loadData();
        },
        error: () => {
          Swal.fire({
            title: 'Error!',
            text: 'Could not delete this file.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      });
    });
  }

  getLeaveFiles(leave: any): any[] {
    if (!leave) {
      return [];
    }
    const files = leave.files ?? leave.attachments ?? [];
    return Array.isArray(files) ? files : [];
  }

  leaveFileCount(leave: any): number {
    return this.getLeaveFiles(leave).length;
  }

  getFileUrl(file: any): string {
    if (!file) {
      return '';
    }
    if (typeof file === 'string') {
      return file;
    }
    if (typeof file.file === 'string') {
      return file.file;
    }
    if (file.file?.file) {
      return file.file.file;
    }
    return file.url || file.attachment || '';
  }

  getFileId(file: any): string | number | null {
    return file?.id ?? file?.file?.id ?? null;
  }

  getFileName(url: string): string {
    if (!url) {
      return 'Attachment';
    }
    return decodeURIComponent(url.split('/').pop() || 'Attachment');
  }

  loadData(): void {
    this.utility.loading = true;
    forkJoin({
      leaves: this.api.get('leaves/', this.token),
      employees: this.api.get('employees/', this.token)
    }).subscribe({
      next: ({ leaves, employees }) => {
        let leaveData: any = leaves ?? [];
        if (!Array.isArray(leaveData)) {
          leaveData = [];
        }
        let empData: any = employees ?? [];
        this.allItems = this.enrichLeaves(leaveData);
        this.employees = empData.map((row: any) => row.employee ?? row);
        this.applyFilters();

        if (this.selectedLeave?.id) {
          this.selectedLeave =
            this.allItems.find(item => item.id === this.selectedLeave.id) ?? this.selectedLeave;
        }
      },
      error: () => {
        Swal.fire({
          title: 'Error',
          text: 'Could not load leave requests.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      },
      complete: () => {
        this.utility.loading = false;
      }
    });
  }

  applyFilters(): void {
    let result = [...this.allItems];

    if (this.filter_date) {
      const month = this.datepipe.transform(this.filter_date, 'yyyy-MM');
      result = result.filter(
        i => this.datepipe.transform(i.from_date, 'yyyy-MM') === month
      );
    }

    const query = this.searchTerm.trim().toLowerCase();
    if (query) {
      result = result.filter(i =>
        (i.employee_name ?? '').toLowerCase().includes(query)
      );
    }

    this.displayItems = result;
    this.list_length = result.length;
    this.page = 1;
  }

  onSearchChange(): void {
    this.applyFilters();
  }

  filterDate(): void {
    this.applyFilters();
  }

  clearFilter(): void {
    this.filter_date = null;
    this.searchTerm = '';
    this.applyFilters();
  }

  getLeaveTypeMeta(type: string) {
    return this.leaveTypes.find(t => t.value === type) ?? {
      value: type,
      label: type,
      badge: 'bg-secondary'
    };
  }

  saveLeave(): void {
    if (!this.isFormValid || this.submitting) {
      return;
    }

    const employee = this.employees.find(i => i.id === this.form.employee_id);
    const body = {
      employee_id: this.form.employee_id,
      employee_name: employee?.name ?? '',
      from_date: this.form.from_date,
      to_date: this.durationMode === 'hours' ? this.form.from_date : this.form.to_date,
      leave_type: this.form.leave_type,
      notes: this.form.notes,
      hours: this.durationMode === 'hours' ? this.form.hours : null
    };

    this.submitting = true;
    const request =
      this.modalMode === 'edit' && this.editingId
        ? this.api.update('leaves/' + this.editingId, body, this.token)
        : this.api.post('leaves/', body, this.token);

    request.subscribe({
      next: () => {
        this.modalService.dismissAll();
        this.loadData();
        Swal.fire({
          title: 'Success',
          text:
            this.modalMode === 'edit'
              ? 'Leave request updated.'
              : 'Leave request created.',
          icon: 'success',
          confirmButtonText: 'OK',
          timer: 2000,
          showConfirmButton: true
        });
      },
      error: () => {
        Swal.fire({
          title: 'Error',
          text: 'Something went wrong. Please try again.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      },
      complete: () => {
        this.submitting = false;
      }
    });
  }

  onDelete(id: string): void {
    Swal.fire({
      title: 'Delete leave request?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel'
    }).then(result => {
      if (!result.isConfirmed) {
        return;
      }

      this.api.delete('leaves/' + id, this.token).subscribe({
        next: () => {
          this.loadData();
          Swal.fire({
            title: 'Deleted',
            text: 'Leave request removed.',
            icon: 'success',
            timer: 2000,
            showConfirmButton: false
          });
        },
        error: () => {
          Swal.fire({
            title: 'Error',
            text: 'Could not delete this request.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      });
    });
  }

  private emptyForm(): LeaveForm {
    return {
      employee_id: null,
      employee_name: null,
      from_date: null,
      to_date: null,
      leave_type: 'ANNUAL',
      notes: null,
      hours: null
    };
  }

  private enrichLeaves(data: any[]): any[] {
    return data.map(item => {
      const hours = this.parseHours(item.hours);
      return {
        ...item,
        hours,
        dayDiff: hours ? hours / 8 : this.calcDays(item.from_date, item.to_date)
      };
    });
  }

  private parseHours(value: any): number | null {
    const hours = Number(value);
    if (!value || Number.isNaN(hours) || hours <= 0) {
      return null;
    }
    return hours;
  }

  private calcDays(from: string, to: string): number {
    const date1 = new Date(from);
    const date2 = new Date(to);
    const timeDiff = date2.getTime() - date1.getTime();
    return Math.floor(timeDiff / (1000 * 3600 * 24)) + 1;
  }

  private toInputDate(value: string): string {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) {
      return value;
    }
    return d.toISOString().slice(0, 10);
  }
}
